import React, { useEffect } from 'react';
import { useUserResourceUsageMetrics } from 'new/hooks/adapters/useUserResourceUsageMetrics';
import { ViewType } from '../../constants/viewTypes';
import { MetricsChartSection } from '../MetricsChartSection';

interface ResourceUsageChartProps {
  resource: 'sink' | 'source' | 'summary';
  title: string;
  viewType: ViewType;
  start: Date;
  end: Date;
  onDatesChanged: (start: Date, end: Date) => void;
}

// Account-scoped Read/Write charts have no resource id, so the operation kind
// is what distinguishes their CSV exports (account-read vs account-write).
const CSV_CATEGORY_BY_RESOURCE: Partial<Record<ResourceUsageChartProps['resource'], string>> = {
  source: 'read',
  sink: 'write',
};

/**
 * Read/Write Operations trend chart, backed by per-day resource usage metrics.
 * Fetches on date-range change and renders through the shared MetricsChartSection.
 */
export const ResourceUsageChart = ({
  resource,
  title,
  viewType,
  start,
  end,
  onDatesChanged,
}: ResourceUsageChartProps) => {
  const { data, loading, fetch } = useUserResourceUsageMetrics(resource, viewType);

  useEffect(() => {
    fetch({ start, end });
  }, [fetch, start, end]);

  return (
    <MetricsChartSection
      title={title}
      viewType={viewType}
      data={data}
      loading={loading}
      start={start}
      end={end}
      onDatesChanged={onDatesChanged}
      csvCategory={CSV_CATEGORY_BY_RESOURCE[resource]}
    />
  );
};
